import React, {useState, useEffect} from 'react';
import {Descriptions, Spin, Checkbox, Typography, Button} from 'antd';
import {io} from 'socket.io-client';
import {useDispatch, useSelector} from "react-redux";
import {setMapCenter, setZoom, toggleFollowVehicle} from "../../../slices/mapSlice";
import {MenuFoldOutlined, MenuUnfoldOutlined, SettingOutlined} from "@ant-design/icons";
import {toggleMapButton, toggleRightButton} from "../../../slices/mapSettingSlice";

const MapSidebarRight = ({vehicleId}) => {

    const {Text} = Typography;
    const dispatch = useDispatch();
    const {user} = useSelector((state) => state.auth);
    const panelOptions = useSelector((state) => state.mapSettings.panelOptions);
    const rightButton = useSelector((state) => state.mapSettings.rightButton);
    const {followVehicle} = useSelector((state) => state.map);
    const [vehicle, setVehicle] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!vehicleId) return;

        setLoading(true);
        const socket = io('https://live.myoctoopidev.com', {
            reconnection: true,
            transports: ['websocket'],
            query: {
                'x-access-token': user?.token,
                'vehicle_id': vehicleId,
            },
        });

        socket.on('live', (incomingData) => {
            if (incomingData.vehicle_id === vehicleId) {
                setVehicle(incomingData);
                if (followVehicle && incomingData.gps) {
                    const newCenter = {lat: incomingData.gps.lat, lng: incomingData.gps.lon};
                    dispatch(setMapCenter(newCenter));
                    dispatch(setZoom(18));
                }
            }
            setLoading(false);
        });

        socket.on('connect_error', (error) => {
            console.error('Socket error:', error);
            setLoading(false);
        });

        return () => {
            socket.disconnect();
        };
    }, [vehicleId, followVehicle, dispatch, user?.token]);

    const handleFollowVehicle = (e) => {
        const isChecked = e.target.checked;
        dispatch(toggleFollowVehicle(isChecked));
        if (!isChecked) {
            dispatch(setMapCenter({lat: 39.9334, lng: 32.8597}));
            dispatch(setZoom(15));
        }
    };

    const renderBatStatus = (status) => {
        switch (status) {
            case "AS_ERROR":
                return "Hatalı";
            case "CHARGING":
                return "Şarjda";
            case "NOT_CHARGING":
                return "Stabil";
            case "AS_NOT_AVAILABLE":
                return "Uygunsuz";
            case "gray":
                return "Uygunsuz";
            case null:
                return "Bilinmiyor";
            default:
                return "-";
        }
    };

    const renderStatusDoor = (status) => {
        switch (status) {
            case "OPENED":
                return "Açık";
            case "CLOSED":
                return "Kapalı";
            case "NOT_AVAILABLE":
                return "Uygun değil";
            case "ERROR":
                return "Hatalı";
            case null:
                return "Bilinmiyor";
            default:
                return "-";
        }
    };

    const adjustDate = (dateString) => {
        if (!dateString) return '-';
        const dateParts = dateString.split(' ');
        const [year, month, day] = dateParts[0].split('-').map(Number);
        const [hour, minute, second] = dateParts[1].split(':').map(Number);

        const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
        date.setUTCHours(date.getUTCHours() + 3);

        const adjustedDate = date.toISOString().replace('T', ' ').slice(0, 19);
        return adjustedDate;
    };

    const AvgFuelConsumption = vehicle && ((vehicle.LiquetEngineTotalFuelUsed / vehicle?.highResVehicleTotalDistance) / 0.7681 * 100).toFixed(2);

    const allItems = {
        plateNumber: {label: 'Plaka', children: vehicle?.plateNumber || '-'},
        totalDistance: {
            label: 'Toplam KM',
            children: vehicle?.highResVehicleTotalDistance ? parseInt(vehicle?.highResVehicleTotalDistance, 10) : '-'
        },
        engineCoolantTemp: {
            label: 'Motor Soğ. Sıv. Sıc.',
            children: `${vehicle?.engineCoolantTemp !== undefined ? vehicle.engineCoolantTemp.toFixed(1) : '0'} °C`
        },
        ambientAirTemp: {
            label: 'Dış Ortam Sıcaklığı',
            children: `${vehicle?.ambientAirTemp !== undefined ? vehicle.ambientAirTemp.toFixed(1) : '0'} °C`
        },
        engineSpeed: {
            label: 'Motor Devri',
            children: `${vehicle?.engineSpeed?.mean !== undefined ? vehicle.engineSpeed.mean.toFixed(0) : '0'} Rpm`
        },
        wheelBasedSpeed: {
            label: 'Araç Hızı',
            children: `${vehicle?.wheelBasedSpeed?.mean !== undefined ? vehicle.wheelBasedSpeed.mean.toFixed(0) : '0'} km/h`
        },
        serviceBrakeAirPressureCircuit1: {
            label: 'Servis Basıncı 1',
            children: `${vehicle?.serviceBrakeAirPressureCircuit1 !== undefined ? vehicle.serviceBrakeAirPressureCircuit1 : '-'} kPa`
        },
        d1_os: {label: 'Kapı 1', children: renderStatusDoor(vehicle?.d1_os || null)},
        d2_os: {label: 'Kapı 2', children: renderStatusDoor(vehicle?.d2_os || null)},
        d3_os: {label: 'Kapı 3', children: renderStatusDoor(vehicle?.d3_os || null)},
        d4_os: {label: 'Kapı 4', children: renderStatusDoor(vehicle?.d4_os || null)},
        alt1: {label: 'Alternatör 1', children: renderBatStatus(vehicle?.alt1 || null)},
        alt2: {label: 'Alternatör 2', children: renderBatStatus(vehicle?.alt2 || null)},
        alt3: {label: 'Alternatör 3', children: renderBatStatus(vehicle?.alt3 || null)},
        actualEnginePercentTorque: {
            label: 'Motor Tork Yüzdesi',
            children: `% ${vehicle?.actualEnginePercentTorque !== undefined ? vehicle.actualEnginePercentTorque.toFixed(0) : '0'}`
        },
        mcuid: {label: 'Seyir Durumu', children: `${vehicle?.mcuid?.uidw1 || '-'}`},
        serviceBrakeAirPressureCircuit2: {
            label: 'Servis Basıncı 2',
            children: `${vehicle?.serviceBrakeAirPressureCircuit2 !== undefined ? vehicle.serviceBrakeAirPressureCircuit2 : '-'} kPa`
        },
        fuelLevel: {
            label: 'Yakıt Seviyesi',
            children: `% ${vehicle?.fuelLevel !== undefined ? vehicle.fuelLevel.toFixed(1) : '-'}`
        },
        AftertreatmentDieselExhaustFluidTank1Level: {
            label: 'Ad Blue Seviyesi',
            children: `% ${vehicle?.AftertreatmentDieselExhaustFluidTank1Level !== undefined ? vehicle.AftertreatmentDieselExhaustFluidTank1Level.toFixed(1) : '-'}`
        },
        acceleratorPedalPosition: {
            label: 'Gaz Pedalı Pozisyonu',
            children: `% ${vehicle?.acceleratorPedalPosition !== undefined ? vehicle.acceleratorPedalPosition.toFixed(0) : '-'}`
        },
        brakePedalPosition: {
            label: 'Fren Pedalı Pozisyonu (%)',
            children: `% ${vehicle?.brakePedalPosition !== undefined ? vehicle.brakePedalPosition.toFixed(0) : '0'}`
        },
        currentGear: {label: 'Vites', children: `${vehicle?.currentGear || '-'}`},
        engineTotalHoursOfOperation: {
            label: 'Toplam Çalışma Saati',
            children: `${vehicle?.engineTotalHoursOfOperation || '-'}`
        },
        chassisNumber: {label: 'Şase Numarası', children: `${vehicle?.chassisNumber || '-'}`},
        vehicle_id: {label: 'Cihaz ID', children: `${vehicle?.vehicle_id || '-'}`},
        LiquetEngineTotalFuelUsed: {
            label: 'Toplam Kullanılan Yakıt [L]',
            children: `${vehicle?.LiquetEngineTotalFuelUsed || '-'}`
        },
        createdAt_last_data: {label: 'Tarih / Saat', children: adjustDate(vehicle?.createdAt_last_data || '')},
        AvgFuelConsumption: {label: 'Ortalama Yakıt Tüketimi', children: `${vehicle ? AvgFuelConsumption : '-'} km/L`},
        ain_ana5: {
            label: 'Akü Voltajı',
            children: <Text
                type={vehicle?.ain?.ana5 < 12 ? 'danger' : ''}>{vehicle?.ain?.ana5?.toFixed(2) || 'Veri Yok'} V</Text>
        },
    };

    if (!vehicleId) {
        return null;
    }

    const toggleSidebar = () => {
        dispatch(toggleRightButton(true));
    };

    const handleDropdownChange = (flag) => {
        dispatch(toggleMapButton());
    };

    return (
        <>
            <Button
                style={{
                    position: 'absolute',
                    height: 100,
                    borderTopLeftRadius: 60,
                    borderBottomLeftRadius: 60,
                    top: '45%',
                    right: rightButton ? 455 : 2,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    transform: rightButton ? 'none' : 'rotate(360deg)',
                    transition: 'right 0.5s',
                }}
                icon={rightButton ? <MenuFoldOutlined/> : <MenuUnfoldOutlined/>}
                onClick={toggleSidebar}
            />
            <div className={`sidebar-right ${rightButton ? 'open' : 'closed'}`}>
                <div style={{...styles.sidebar, minHeight: loading ? '300px' : 'auto'}}>
                    <div style={styles.header}>
                        <h3 style={styles.plateNumber}>
                            {vehicle ? `${vehicle.plateNumber} - ${vehicle.operationNumber}` : 'Yükleniyor...'}
                        </h3>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <Button type="text" onClick={handleDropdownChange} icon={<SettingOutlined/>}/>
                            <Checkbox
                                checked={followVehicle}
                                onChange={(e) => handleFollowVehicle(e)}
                                style={styles.checkbox}
                                disabled={!vehicle}
                            >
                                Aracı Takip Et
                            </Checkbox>
                        </div>
                    </div>
                    {loading ? (
                        <div style={styles.loadingContainer}>
                            <Spin size="small"/>
                        </div>
                    ) : (
                        <Descriptions bordered column={1} size="small">
                            {panelOptions.map((option) => {
                                const item = allItems[option];
                                return item ? (
                                    <Descriptions.Item label={item.label} key={option} style={{fontSize: '13px'}}>
                                        {item.children}
                                    </Descriptions.Item>
                                ) : null;
                            })}
                        </Descriptions>
                    )}
                </div>
            </div>
        </>
    );

};

const styles = {
    sidebarContainer: {
        height: '90vh',
        overflowY: 'auto',
        position: 'absolute',
        right: 5,
        top: 0,
        zIndex: 999,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: '10px',
        transition: 'left 0.5s ease-in-out',
    },
    sidebar: {
        width: '460px',
        padding: '15px',
        borderRadius: 5,
        borderColor: 'lightgray',
        backgroundColor: 'white',
        borderLeft: '1px solid #d9d9d9',
        maxHeight: '80vh',
        overflowY: 'auto',
        transition: 'left 0.5s ease-in-out',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px',
    },
    plateNumber: {
        margin: 0,
    },
    checkbox: {
        marginLeft: '10px',
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '1000px',
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(3px)',
        zIndex: 10,
    },
};

export default MapSidebarRight;
